import type { ValidationErrors, ValidatorFn } from '@angular/forms';
import { UntypedFormControl, Validators } from '@angular/forms';

import { emailValidator, noWhitespaceValidator } from '@core/common-legacy';

import type {
  AnyDynamicCommonInputConfig,
  DynamicEmailInputConfig,
  DynamicNumberInputConfig,
  DynamicTextInputConfig,
} from '../../../types';

export abstract class DynamicFormInput<
  Config extends AnyDynamicCommonInputConfig = AnyDynamicCommonInputConfig,
> {
  public readonly kind = <const>'DynamicFormInput';
  public type: Config['type'];
  public formError: ValidationErrors = {};

  public control: UntypedFormControl;
  constructor(public config: Config) {
    this.type = config.type;
    this.control = new UntypedFormControl(config.value, [
      ...this.validators(),
      ...this.customValidators(),
    ]);
  }

  public isHidden(): boolean {
    return !!this.config.hidden;
  }

  public isRemovable(): boolean {
    return !!this.config.removable;
  }

  protected validators(): ValidatorFn[] {
    return this.config.required ? [Validators.required] : [];
  }

  protected customValidators(): ValidatorFn[] {
    return this.config.validators ? this.config.validators : [];
  }
}

export class TextInput extends DynamicFormInput<DynamicTextInputConfig> {
  constructor(public config: DynamicTextInputConfig) {
    super(config);
    this.formError = this.config.validationPattern
      ? { pattern: this.config.validationPattern.errorMessage }
      : {};
  }

  protected validators(): ValidatorFn[] {
    const validators = [
      noWhitespaceValidator,
      ...(this.config.validationPattern
        ? [Validators.pattern(this.config.validationPattern.format)]
        : []),
      ...(this.config.minLength ? [Validators.minLength(this.config.minLength)] : []),
      ...(this.config.maxLength ? [Validators.maxLength(this.config.maxLength)] : []),
    ];

    return super.validators().concat(validators);
  }
}

export class NumberInput extends DynamicFormInput<DynamicNumberInputConfig> {
  protected validators(): ValidatorFn[] {
    const validators = [
      noWhitespaceValidator,
      ...(this.config.min ? [Validators.min(this.config.min)] : []),
      ...(this.config.max ? [Validators.max(this.config.max)] : []),
    ];

    return super.validators().concat(validators);
  }
}

export class EmailInput extends DynamicFormInput<DynamicEmailInputConfig> {
  protected validators(): ValidatorFn[] {
    return super.validators().concat([emailValidator]);
  }
}
